import { getCached, cacheBlob } from "./fileCache";
import { fetchTelegramFileBlob } from "./tgFileFetch";
import { shouldPreviewAsText } from "./textFiles";
import { getFileUrl } from "./telegram";

const CACHE_LIMIT_BYTES = 25 * 1024 * 1024;

function blobFromCached(cached, mime) {
  if (cached instanceof Blob) return cached;
  return new Blob([cached], { type: mime || "application/octet-stream" });
}

/**
 * Load file bytes for preview: inline-text first (notes), IndexedDB cache,
 * then same-origin B2 proxy. Returns a blob URL that works for
 * PDF / audio / video / image in-browser.
 *
 * For previewable media above the cache threshold (where buffering the whole
 * blob into RAM is wasteful and slow), returns the proxy URL directly so the
 * browser can stream via range requests instead.
 */
export async function loadPreviewBlob(file) {
  const mime = file.mime || "application/octet-stream";

  // Notes carry their content inline in the manifest — synthesize a blob
  // straight from the string so the existing preview pipeline keeps working
  // without a separate fetch.
  if (file.storage === "note-inline" || (file.kind === "note" && typeof file.content === "string")) {
    const blob = new Blob([file.content || ""], { type: mime });
    return { blob, blobUrl: URL.createObjectURL(blob), source: "inline" };
  }

  const cached = await getCached(file.id);
  if (cached) {
    const blob = blobFromCached(cached, mime);
    return { blob, blobUrl: URL.createObjectURL(blob), source: "cache" };
  }

  if (file.storage !== "b2" || !file.b2Key) {
    throw new Error("File has no fetchable storage location.");
  }

  // Heavy streamable media (video/audio mostly, also large images/PDFs): skip
  // the full-blob fetch and let the browser stream from the proxy URL via
  // <video src=…> / <embed src=…>. Saves seconds-to-minutes on multi-hundred-MB
  // previews and keeps RAM flat.
  if (file.size && file.size > CACHE_LIMIT_BYTES && isStreamableMime(mime)) {
    return { blob: null, blobUrl: getFileUrl(file), source: "direct" };
  }

  const blob = await fetchTelegramFileBlob(file, mime);
  // Don't cache giant B2 files in IndexedDB — it would blow browser quota.
  if (blob.size <= CACHE_LIMIT_BYTES) {
    await cacheBlob(file.id, blob).catch(() => {});
  }

  return { blob, blobUrl: URL.createObjectURL(blob), source: "b2" };
}

function isStreamableMime(mime) {
  return (
    mime.startsWith("video/") ||
    mime.startsWith("audio/") ||
    mime.startsWith("image/") ||
    mime === "application/pdf"
  );
}

export async function loadPreviewText(file, blob) {
  if (file.content != null) return file.content;
  if (!blob) return null;
  const isText = await shouldPreviewAsText(file, blob);
  if (!isText) return null;
  try {
    return await blob.text();
  } catch {
    return null;
  }
}
